import React from 'react';

import {
  Typography,
  Button,
  TextField,
  IconButton,
  Modal,
  Divider,
} from '@mui/material';
import { Box } from '@mui/system';
import ExpandableTypography from 'src/common/components/ExpandableTypography';
import { SaveSheet } from 'src/common/types/SaveSheet';

import { ReactComponent as CancelIcon } from '../../../../common/assets/icons/cancel_icon_gray.svg';
import { ReactComponent as CloseIcon } from '../../../../common/assets/icons/close_icon_filled.svg';
import { ReactComponent as EditIcon } from '../../../../common/assets/icons/edit_icon_underlined.svg';
import ScanThumbnail from '../../ScanThumbnail/ScanThumbnail';

import './SearchedTextArea.scss';

type Props = {
  searchType: 'text' | 'scan';
  searchText?: string;
  refreshResults?: (text: string) => void;
  filename?: string;
  savedSheet: SaveSheet | null | undefined;
  allowNoteTextEditing: boolean;
};

const modalStyle = {
  position: 'absolute' as const,
  width: '100%',
  height: '100%',
  overflowY: 'auto',
};

const SearchedTextArea: React.FC<Props> = ({
  searchType,
  searchText,
  refreshResults,
  filename,
  savedSheet,
  allowNoteTextEditing,
}) => {
  const [editedSearchText, setEditedSearchText] = React.useState(
    searchText ?? ''
  );

  // Used by the text search
  React.useEffect(() => {
    setEditedSearchText(searchText ?? '');
  }, [searchText]);

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditedSearchText(event.target.value ?? '');
  };

  const onKeyUp = (event: any) => {
    if (event.code === 'Enter') {
      if (editedSearchText.trim().length > 0) {
        if (refreshResults) {
          refreshResults(editedSearchText.trim());
        }
      }
    }
  };

  // Used by the scan search
  const [openEditPopup, setOpenEditPopup] = React.useState(false);

  const confirmChanges = () => {
    if (editedSearchText.trim().length > 0) {
      if (refreshResults) {
        refreshResults(editedSearchText.trim());
        setOpenEditPopup(false);
      }
    }
  };

  const closePopup = () => {
    setOpenEditPopup(false);
    setEditedSearchText(searchText ?? '');
  };

  return (
    <>
      {searchType === 'text' && (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
          }}
        >
          <TextField
            className='edit-keyword-text'
            multiline
            onKeyUp={onKeyUp}
            placeholder='Type description of the procedure'
            size='small'
            value={editedSearchText}
            onChange={handleSearchInputChange}
            variant={'filled'}
            minRows={5}
            color='secondary'
            sx={() => ({
              height: '100%',
              overflow: 'auto',
              zIndex: 9,
              alignSelf: 'baseline',
              borderColor: 'primary',
              opacity: 1.0,
              width: '100%',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 0,
                },
                '&:hover fieldset': {
                  border: 0,
                },
                '&.Mui-focused fieldset': {
                  border: 0,
                },
              },
            })}
            InputProps={{
              style: {
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontFamily: 'SFProDisplay-Medium',
                color: '#ffffff',
                fontSize: 16,
                lineHeight: 1,
                marginLeft: 36,
                marginRight: 10,
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 12,
                paddingRight: 12,
              },
            }}
          />
          {editedSearchText.length > 0 && (
            <IconButton
              sx={{
                background: 'transparent',
                height: '38px',
                width: '38px',
              }}
              onClick={() => setEditedSearchText('')}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}

      {searchType === 'scan' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '24px',
            width: '100%',
            marginLeft: savedSheet?.clinicNote ? '0px' : '32px',
          }}
        >
          <ExpandableTypography
            lineCount={7}
            color={savedSheet?.clinicNote ? 'rgba(0, 33, 69, 0.70)' : 'white'}
            editColor={savedSheet?.clinicNote ? '#0CBDD4' : '#266071'}
            fontSize={16}
          >
            {searchText}
          </ExpandableTypography>
          {allowNoteTextEditing && (
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '16px',
                borderRadius: '16px',
                backgroundColor: 'lightBlue.main',
                width: '120px',
                minHeight: '120px',
              }}
            >
              {filename ? (
                <ScanThumbnail filename={filename} />
              ) : (
                <Typography
                  textAlign='center'
                  fontSize='14px'
                  paddingTop='32px'
                  paddingBottom='32px'
                >
                  No Preview
                </Typography>
              )}

              <Button
                sx={{
                  marginTop: 'auto',
                  padding: 0,
                }}
                onClick={() => setOpenEditPopup(true)}
                variant='text'
                color='darkBlue'
                startIcon={<EditIcon fill='#266071' />}
              >
                Edit
              </Button>
            </Box>
          )}
          <Modal open={openEditPopup} onClose={() => closePopup()}>
            <Box sx={modalStyle}>
              <Box
                className='edit-note-modal-container'
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: '100%',
                  backgroundColor: 'appMerino.main',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '90px',
                    width: '100%',
                  }}
                >
                  <Button
                    onClick={() => closePopup()}
                    variant='text'
                    startIcon={<CancelIcon />}
                  >
                    <Typography color='text.secondary'>Cancel</Typography>
                  </Button>
                  <Button
                    onClick={confirmChanges}
                    variant='contained'
                    color='primary'
                    disableElevation
                    sx={{
                      width: 'auto',
                      height: 48,
                      borderRadius: '15px',
                    }}
                  >
                    <Typography color='white'>Confirm Сhanges</Typography>
                  </Button>
                </Box>
                <Divider sx={{ marginTop: '18px' }} />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    backgroundColor: 'appMerino.main',
                  }}
                >
                  <Typography
                    sx={{ mb: '24px' }}
                    variant='subtitle1'
                    color='secondary'
                  >
                    Note
                  </Typography>
                  <TextField
                    onChange={handleSearchInputChange}
                    variant='standard'
                    multiline
                    fullWidth
                    value={editedSearchText}
                    sx={{
                      mb: '30px',
                    }}
                    InputProps={{ disableUnderline: true, spellCheck: false }}
                    inputProps={{ style: { color: '#002145B2' } }}
                  />
                  <Divider />
                </Box>
              </Box>
            </Box>
          </Modal>
        </Box>
      )}
    </>
  );
};

export default SearchedTextArea;
